import { isDevUser } from './devUsers';

export const COMPONENT_LABELS_EVENT = 'cmf:component-labels-refresh';
const LOCAL_STORAGE_KEY = 'cmf:debugComponentLabels';

const ENV_KEYS = [
  'REACT_APP_SHOW_COMPONENT_LABELS',
  'VITE_SHOW_COMPONENT_LABELS',
  'VITE_FEATURE_COMPONENT_LABELS'
] as const;

const parseBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'off'].includes(normalized)) {
      return false;
    }
  }
  return null;
};

const readEnvValue = (): unknown => {
  const importMetaEnv =
    typeof import.meta !== 'undefined' ? (import.meta as { env?: Record<string, unknown> }).env ?? null : null;
  if (importMetaEnv) {
    for (const key of ENV_KEYS) {
      if (key in importMetaEnv) {
        return importMetaEnv[key];
      }
    }
  }

  if (typeof process !== 'undefined' && process.env) {
    for (const key of ENV_KEYS) {
      if (process.env[key] !== undefined) {
        return process.env[key] as unknown;
      }
    }
  }

  return undefined;
};

const resolveFromStorage = (): boolean | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    return parseBoolean(stored);
  } catch {
    return null;
  }
};

export const areComponentLabelsEnabled = (userId?: string | null): boolean => {
  // If userId is provided, check if user is a dev user first
  // If not a dev user, component labels are disabled regardless of flags
  if (userId !== undefined && userId !== null) {
    if (!isDevUser(userId)) {
      return false;
    }
  }

  const envValue = parseBoolean(readEnvValue());
  const baseEnabled = envValue ?? true;

  const storageOverride = resolveFromStorage();
  if (storageOverride !== null) {
    return storageOverride;
  }

  return baseEnabled;
};

export const setComponentLabelOverride = (value: boolean | null): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (value === null) {
      window.localStorage.removeItem(LOCAL_STORAGE_KEY);
    } else {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, value ? 'true' : 'false');
    }
    window.dispatchEvent(new Event(COMPONENT_LABELS_EVENT));
  } catch {
    // Ignore storage write failures; callers can fall back to env.
  }
};
